<template>
  <div>
    <page-header
      :title="$t('company_limits_title')"
      icon="mdi-shield-account"
      icon-color="primary"
    >
      <template #actions>
        <primary-button @click="createDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_new_limit") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar :searchable="false">
      <template #filters>
        <v-col cols="auto">
          <autocomplete
            v-model="selectedCompanyFilter"
            :items="formattedCompanies"
            item-value="id"
            item-text="name"
            :label="$t('company')"
            hide-details
            color="white"
            clearable
            multiple
            select-all
            style="min-width: 200px"
          />
        </v-col>
        <v-col cols="auto">
          <autocomplete
            v-model="selectedRestaurantFilter"
            :items="formattedRestaurants"
            item-value="id"
            item-text="name"
            :label="$t('restaurant')"
            hide-details
            color="white"
            clearable
            multiple
            select-all
            style="min-width: 200px"
          />
        </v-col>
        <v-col cols="auto">
          <text-field
            v-model="search"
            :label="$t('search_limits')"
            kind="search"
            hide-details
            color="white"
          />
        </v-col>
      </template>
    </page-toolbar>

    <div v-if="isLoading" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" size="64" />
    </div>

    <div v-else class="px-6 mt-2">
      <limits-data-table
        :items="limits"
        :headers="headers"
        :loading="isLoading"
        :restaurants="restaurants"
        :companies="companies"
        :can-edit="() => true"
        @edit="editingLimit = $event; configDialog = true"
        @delete="deleteLimit($event)"
        @toggle="toggleLimitState($event)"
      >
        <template #no-data>
          <empty-state
            icon="mdi-shield-off"
            :title="$t('no_limits_defined')"
            :subtitle="$t('no_limits_subtitle')"
            class="ma-4"
          >
            <primary-button @click="createDialog = true">
              <v-icon left>mdi-plus-circle</v-icon>
              {{ $t("add_new_limit") }}
            </primary-button>
          </empty-state>
        </template>
      </limits-data-table>
    </div>

    <create-limit-dialog
      v-model="createDialog"
      :company-id="null"
      @created="handleLimitCreated"
    />

    <limit-configuration-dialog
      v-if="editingLimit"
      v-model="configDialog"
      :limit="editingLimit"
      :company-id="null"
      :readonly="false"
      @saved="loadLimits"
    />
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import setLang from "@/mixins/setLang";
import hasLimitTable from "@/mixins/hasLimitTable";
import EmptyState from "@/components/global/EmptyState.vue";
import LimitsDataTable from "@/components/limits/LimitsDataTable.vue";
import limitService from "@/services/limit";
import companyService from "@/services/company";
import restaurantService from "@/services/restaurant";
import CreateLimitDialog from "@/components/limitConfiguration/dialogs/CreateLimitDialog.vue";
import LimitConfigurationDialog from "@/components/limitConfiguration/dialogs/LimitConfigurationDialog.vue";
import { normalizeLimitData, sortLimitsByPriority } from "@/utils/limitHelpers";

export default {
  name: "AdminLimits",
  mixins: [hasSnackbar, setLang, hasLimitTable],
  components: {
    EmptyState,
    LimitsDataTable,
    CreateLimitDialog,
    LimitConfigurationDialog,
  },
  data() {
    return {
      isLoading: false,
      search: "",
      allLimits: [],
      companies: [],
      restaurants: [],
      createDialog: false,
      configDialog: false,
      editingLimit: null,
      selectedCompanyFilter: [],
      selectedRestaurantFilter: [],
      companyRestaurantsMap: {}, // { [companyId]: number[] } — cached per company
    };
  },
  computed: {
    formattedCompanies() {
      return this.companies.map((c) => ({
        ...c,
        name: this.toLatin(c.name),
      }));
    },
    // IDs of restaurants that are partners of the currently-selected companies.
    // null means "no company filter active" → show all.
    selectedCompanyRestaurantIds() {
      const allSelected =
        this.selectedCompanyFilter.length === 0 ||
        this.selectedCompanyFilter.length === this.companies.length;
      if (allSelected) return null;
      const ids = new Set();
      for (const cId of this.selectedCompanyFilter) {
        (this.companyRestaurantsMap[cId] || []).forEach((id) => ids.add(id));
      }
      return [...ids];
    },
    formattedRestaurants() {
      let pool = this.restaurants;
      if (this.selectedCompanyRestaurantIds !== null) {
        // company filter is active — show only partner restaurants
        pool = pool.filter((r) =>
          this.selectedCompanyRestaurantIds.includes(Number(r.id))
        );
      }
      // no company filter → show all restaurants
      return pool.map((r) => ({ ...r, name: this.toLatin(r.name) }));
    },
    usedRestaurantIds() {
      return [
        ...new Set(
          this.allLimits
            .map((l) => Number(l.restaurant_id))
            .filter((id) => !isNaN(id) && id !== 0)
        ),
      ];
    },
    limits() {
      let result = this.allLimits;

      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter((l) => l.name?.toLowerCase().includes(q));
      }

      // empty array = no filter active = show all
      if (this.selectedCompanyFilter.length > 0) {
        result = result.filter((l) =>
          this.selectedCompanyFilter.includes(l.company_id)
        );
      }

      if (this.selectedRestaurantFilter.length > 0) {
        result = result.filter(
          (l) =>
            !l.restaurant_id ||
            this.selectedRestaurantFilter.includes(l.restaurant_id)
        );
      }

      return [...result].sort(sortLimitsByPriority);
    },
    headers() {
      if (!this.$t) return [];
      return [
        { text: "ID", value: "id", align: "start", width: "80px" },
        { text: this.$t("name"), value: "name", sortable: true },
        { text: this.$t("company"), value: "company_id", sortable: true },
        { text: this.$t("restaurant"), value: "restaurant_id", sortable: true },
        {
          text: this.$t("limit_type"),
          value: "params.limit_definition.active",
          sortable: true,
        },
        { text: this.$t("time_limit"), value: "params.timing.active", sortable: true },
        {
          text: this.$t("who_pays"),
          value: "params.payment_logic.active",
          sortable: true,
        },
        {
          text: this.$t("what_can_order"),
          value: "params.ordering_rules.active",
          sortable: true,
        },
        { text: this.$t("active"), value: "state", align: "center", sortable: true },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          sortable: false,
        },
      ];
    },
  },
  watch: {
    selectedCompanyFilter(newVal) {
      this.onCompanyFilterChange(newVal);
    },
  },
  mounted() {
    this.loadMeta();
  },
  methods: {
    // Admin can always edit any limit
    canEditLimit() {
      return true;
    },
    async onCompanyFilterChange(selectedIds) {
      const allSelected =
        selectedIds.length === 0 || selectedIds.length === this.companies.length;

      if (allSelected) {
        // no company filter → clear restaurant filter too (empty = show all)
        this.selectedRestaurantFilter = [];
        return;
      }

      // Fetch partner restaurants for companies not yet in cache
      const missing = selectedIds.filter((id) => !(id in this.companyRestaurantsMap));
      if (missing.length > 0) {
        try {
          const responses = await Promise.all(
            missing.map((id) => companyService.getCompanyRestaurants(id))
          );
          responses.forEach((res, i) => {
            const rIds = (res?.data || []).map((r) => Number(r.id));
            this.$set(this.companyRestaurantsMap, missing[i], rIds);
          });
        } catch (error) {
          // non-critical — restaurant filter just stays as-is
        }
      }

      // Update restaurant filter to union of all partner restaurants
      const allPartnerIds = new Set();
      for (const cId of selectedIds) {
        (this.companyRestaurantsMap[cId] || []).forEach((id) => allPartnerIds.add(id));
      }
      this.selectedRestaurantFilter = this.restaurants
        .filter((r) => allPartnerIds.has(Number(r.id)))
        .map((r) => r.id);
    },
    async loadMeta() {
      this.isLoading = true;
      try {
        const [companiesResponse, restaurantsResponse] = await Promise.all([
          companyService.getCompanies(),
          restaurantService.getAllRestaurants(),
        ]);
        this.companies = (companiesResponse?.data || []).filter(
          (c) => Number(c.contract) === 4
        );
        this.restaurants = restaurantsResponse?.data || [];
        // both filters start empty — "nothing selected" means "show all"
      } catch (error) {
        // meta is non-critical
      }
      await this.loadLimits();
    },
    async loadLimits() {
      this.isLoading = true;
      try {
        const limitsData = await limitService.getAllLimits({});
        this.allLimits = limitsData.map((limit) => normalizeLimitData(limit));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_limits"));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
